import React, { useState } from 'react';
import { Button } from './button';
import { Card, CardContent } from './card';
import { Palette, Check } from 'lucide-react';

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  label?: string;
  showGradient?: boolean;
}

const predefinedColors = [
  '#3b82f6', // blue
  '#ef4444', // red
  '#10b981', // emerald
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#84cc16', // lime
  '#f97316', // orange
  '#ec4899', // pink
  '#6366f1', // indigo
  '#14b8a6', // teal
  '#a855f7', // purple
];

const predefinedGradients = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
  'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
];

export const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  label = 'Color',
  showGradient = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [customColor, setCustomColor] = useState('#3b82f6');

  const handleColorSelect = (color: string) => {
    onChange(color);
    setIsOpen(false);
  };

  const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const color = e.target.value;
    setCustomColor(color);
    onChange(color);
  };

  const isGradient = value.startsWith('linear-gradient');

  return (
    <div className="space-y-2">
      {label && <label className="text-sm font-medium">{label}</label>}
      <div className="relative">
        <Button
          variant="outline"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full justify-start gap-2"
        >
          <div
            className="w-4 h-4 rounded border"
            style={{
              background: value || '#3b82f6',
            }}
          />
          <span>{isGradient ? 'Gradient' : value || 'Select Color'}</span>
          <Palette className="ml-auto h-4 w-4" />
        </Button>

        {isOpen && (
          <Card className="absolute top-full left-0 right-0 z-50 mt-1">
            <CardContent className="p-4 space-y-4">
              {/* Solid Colors */}
              <div>
                <h4 className="text-sm font-medium mb-2">Solid Colors</h4>
                <div className="grid grid-cols-6 gap-2">
                  {predefinedColors.map((color) => (
                    <button
                      key={color}
                      onClick={() => handleColorSelect(color)}
                      className="w-8 h-8 rounded border-2 hover:scale-110 transition-transform"
                      style={{ backgroundColor: color }}
                    >
                      {value === color && (
                        <Check className="w-4 h-4 text-white m-auto" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Color Input */}
              <div>
                <h4 className="text-sm font-medium mb-2">Custom Color</h4>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={customColor}
                    onChange={handleCustomColorChange}
                    className="w-12 h-8 rounded border cursor-pointer"
                  />
                  <input
                    type="text"
                    value={customColor}
                    onChange={(e) => setCustomColor(e.target.value)}
                    placeholder="#3b82f6"
                    className="flex-1 px-2 py-1 text-sm border rounded"
                  />
                </div>
              </div>

              {/* Gradients */}
              {showGradient && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Gradients</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {predefinedGradients.map((gradient, index) => (
                      <button
                        key={index}
                        onClick={() => handleColorSelect(gradient)}
                        className="h-8 rounded border-2 hover:scale-105 transition-transform relative"
                        style={{ background: gradient }}
                      >
                        {value === gradient && (
                          <Check className="w-4 h-4 text-white absolute inset-0 m-auto" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

